import { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";
import { MS_TEAMS_INTEGRATION_FLAG } from "@/lib/launchdarkly/flags";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Panel } from "@/components/ui/panel";
import { Switch } from "@/components/ui/switch";
import { NOTIFICATION_CATEGORY_I18N, WEEKLY_SUMMARY_CODE } from "@/lib/notification-categories";
import { useNotificationSettings } from "@/hooks/use-notification-settings";
import { useSaveStatus } from "@/hooks/use-save-status";
import { AutosaveStatusPill } from "@/components/shared/autosave-status-pill";
import { useStore } from "@/stores/store-context";
import { digestRefusalCode, isDigestSent } from "@/lib/integrations/msteams-link";
import { useUserTeamsLinks, useUserTeamsLinkWriters } from "@/hooks/use-user-teams-links";
import type { INotificationSetting } from "@/types/user";

// Group the category toggles into sections (QA: "break options into sections —
// Updates / Collaboration / Proactive"). Comments + mentions sit under
// Collaboration to match the spec §4 Action-Required model (Collaboration =
// comments, mentions, team assignments/approvals). Any category not listed here
// still renders, under a trailing "Other" section, so a new backend category is
// never silently hidden.
const SECTIONS: { titleKey: string; codes: string[] }[] = [
  {
    titleKey: "profile_settings.notifications.section_updates",
    codes: ["status_change", "field_change", "deletion", "assignment"],
  },
  {
    titleKey: "profile_settings.notifications.section_collaboration",
    codes: ["comment", "mention", "collaboration", "shared_dashboard"],
  },
  {
    titleKey: "profile_settings.notifications.section_proactive",
    codes: ["sla", "time_off_conflict"],
  },
  { titleKey: "profile_settings.notifications.section_account", codes: ["account"] },
];

/** The section that holds every `teams_only` category, appended after the rest.
 *  Separate from `SECTIONS` because its membership is a property the API states per
 *  row, not a code list this file keeps in step with the backend. */
const TEAMS_ONLY_SECTION_KEY = "profile_settings.notifications.section_teams_only";

/** The width of one switch column, so a row's switches line up under the header
 *  labels that name them. A shared constant rather than the same utility typed twice:
 *  the two have to agree or the columns read as unlabelled. */
const COLUMN = "w-24";

type TSection = { titleKey: string; items: INotificationSetting[]; teamsOnly: boolean };

/** Bucket the flat settings list into sections, in section order, dropping
 * empty sections and collecting any unmapped categories into a trailing
 * "Other" group so nothing the backend sends ever disappears.
 *
 * `teams_only` rows are held out of all of that and appended as their own section —
 * and only when the Teams column is being shown at all, because a row with no bell
 * switch has nothing left on it otherwise. See the component docstring. */
function groupIntoSections(
  settings: INotificationSetting[],
  { showTeams }: { showTeams: boolean },
): TSection[] {
  const inApp = settings.filter((s) => !s.teams_only);
  const byCode = new Map(inApp.map((s) => [s.code, s]));
  const mapped = new Set(SECTIONS.flatMap((s) => s.codes));
  const sections: TSection[] = SECTIONS.map((section) => ({
    titleKey: section.titleKey,
    items: section.codes
      .map((code) => byCode.get(code))
      .filter((s): s is INotificationSetting => Boolean(s)),
    teamsOnly: false,
  })).filter((section) => section.items.length > 0);

  const other = inApp.filter((s) => !mapped.has(s.code));
  if (other.length > 0) {
    sections.push({
      titleKey: "profile_settings.notifications.section_other",
      items: other,
      teamsOnly: false,
    });
  }

  const teamsOnly = settings.filter((s) => s.teams_only);
  if (showTeams && teamsOnly.length > 0) {
    sections.push({ titleKey: TEAMS_ONLY_SECTION_KEY, items: teamsOnly, teamsOnly: true });
  }
  return sections;
}

/** The per-category notification switches — one column for the bell and email, and a
 *  second for Microsoft Teams where the person can actually receive one (NG-1187).
 *
 *  **The Teams column is absent, never disabled — and it gates on the person, not on
 *  their workspaces.** `hasMatchedLink`, not `hasConnectedWorkspaces`: the two differ
 *  for exactly the case that matters, a plain member of a workspace whose admin
 *  enabled Teams but who has never linked their own Microsoft account. The block above
 *  this one *does* render for them, correctly — it is what offers them Connect — but a
 *  column of switches would be a column of controls that can deliver nothing, and a
 *  disabled switch is a promise that turning it on would do something. A **matched**
 *  link is enough; the Teams app need not be installed yet, because these are settings
 *  rather than deliveries and configuring before installing is reasonable.
 *
 *  Both gates read false while the link read is in flight, so the column appears when
 *  the payload lands rather than flashing an unusable one first.
 *
 *  The read goes through `ensureFetched`, which no-ops when the block above has
 *  already started it — two surfaces on one page wanting the same payload is what that
 *  method exists for, and a bare `fetch()` here would double the request for everybody
 *  who opens this page.
 *
 *  **The two switches are independent, and the copy has to say so**, which is why the
 *  section headers carry column labels at all: two unlabelled switches on a row read as
 *  a control and its confirmation. Nothing marks a Teams answer as inherited: the row
 *  carries `teams_is_default`, and a caption saying so under every untouched switch was
 *  clutter that told a reader nothing they could act on (Tom Mason, 2026-09-10). There
 *  is no reset-to-default affordance in this MVP either — the API takes `null` for one
 *  when it earns a place; flipping a switch simply stores a choice.
 *
 *  **A `teams_only` category has no in-app or email delivery behind it at all**
 *  (NG-1641: the Monday summary is a Teams card and nothing else), so its row carries
 *  the Teams switch alone. Two consequences follow, and both are the same judgement
 *  the Teams column already makes.
 *
 *  It is **dropped, not disabled, where `showTeams` is false** — with no Teams column
 *  there is nothing on the row left to configure, and a lone bell switch there would
 *  promise a notification this category never sends. That is strictly the column rule
 *  applied to a row that is all column.
 *
 *  And where it is shown it gets **its own section rather than the trailing "Other"**.
 *  "Other" means "the backend sent a code this form has no copy for", and the copy is
 *  what makes it a safe dumping ground; a Teams-only category has copy and a
 *  deliberate shape, so filing it there would say the opposite of what is true and put
 *  a one-switch row among two-switch ones under a heading explaining neither. Its
 *  header names the Teams column only, because a second column label over nothing
 *  reads as a switch that failed to render.
 *
 *  **Its row name is a `<Label>` like every other, but an unassociated one.** The
 *  styling has one definition — never a copy of `ui/label`'s class string — and the
 *  missing `htmlFor` is a naming decision rather than an oversight: base-ui's `Switch`
 *  turns a `<label for>` into an `aria-labelledby` on the switch root (verified in the
 *  rendered DOM, not assumed), and `aria-labelledby` outranks `aria-label`, so
 *  pointing this label at the Teams switch would rename it from "<Category> in Teams"
 *  to the bare category — losing the column half of the name that keeps two switches
 *  on the ordinary rows distinguishable, and breaking every locator that reads it. So
 *  the control is named the way `Label.doc.json` prescribes for a control that cannot
 *  take the association: by its own `aria-label`.
 *
 *  **The Monday summary row also carries a "Send my Monday summary now" button**
 *  (NG-1641) — a demo affordance Product asked for on 2026-09-23, which may be taken
 *  out again. Gated on that row's category **code**, not on `teams_only`: the shape is
 *  a growing set and the button sends one specific category's card, so gating on the
 *  shape would draw an identically-named second button the day a second Teams-only
 *  category ships.
 *
 *  **It carries no flag of its own, by decision.** It renders on a `teams_only` row,
 *  and that row exists only where `showTeams` does — the `ms-teams-integration` flag
 *  **and** a matched link of this person's — so it is already dark everywhere the
 *  Teams column is, and a second flag would gate a control behind a gate its only
 *  container already holds. Nothing goes in `feature-gates.txt` for the same reason:
 *  a manifest entry claiming a path this file does not own is worse than none.
 *
 *  **It sits in the row's text column, under the hint, not in the switch column.**
 *  The switch column is a grid of controls that all answer the same question, and a
 *  button whose press has an effect right now does not belong in a column of
 *  settings — it would also break the alignment the column constant exists to keep.
 *
 *  **Disabled while that row's own Teams switch is off**, because the server refuses
 *  exactly that case (`switch_off`) and a button that can only produce an error is a
 *  worse way to say so than a disabled one. Disabled again while a send is in flight,
 *  which is what `pendingAction === "send_digest"` is for: a second press is not
 *  destructive — the endpoint answers it with a 202 for the next free day, so it sends
 *  a *second* card rather than refusing — but a control that looks idle mid-round-trip
 *  reads as one that did nothing, and an accidental double press costs a real card.
 *
 *  **Its accessible name is its visible text**, not a longer one — the switch beside
 *  it is called "<Category> in Teams" and neither name contains the other, so no
 *  role-name lookup on this row resolves to two elements (NG-847).
 */
export const NotificationsForm = observer(function NotificationsForm() {
  const { t } = useTranslation();
  const { user, workspace, featureFlags } = useStore();
  const slug = workspace.currentWorkspace?.slug;
  const { settings, loadFailed } = useNotificationSettings(slug);

  // Read the same way `teams-link-block.tsx` does: an unresolved flag is false, so the
  // column appears when the flag resolves on rather than flashing on a page it may not
  // belong to.
  const teamsFlagEnabled = featureFlags.isEnabled(MS_TEAMS_INTEGRATION_FLAG, false);
  // One cached read, shared with the profile block and the bell; a failed read leaves
  // `hasMatchedLink` false, which renders the column absent.
  const userTeamsLink = useUserTeamsLinks({ enabled: teamsFlagEnabled });
  const teamsLinkWriters = useUserTeamsLinkWriters();
  const showTeams = teamsFlagEnabled && userTeamsLink.hasMatchedLink;

  useEffect(() => {
    if (loadFailed) toast.error(t("profile_settings.notifications.load_error"));
    // Keyed on the failure alone: the toast is for it, not for a language change.
  }, [loadFailed]);

  // Whether the switches' writes were stored (NG-2814): the switch flips at
  // once, so only this status says the server has it — Saving… while any
  // write is out, Save failed if one failed, and "Up to date" once all of them
  // answered. Counted, so a second flip made mid-write doesn't settle early.
  const switchSave = useSaveStatus();
  const switchWrites = useRef({ inFlight: 0, failed: false });
  const saveSwitch = async (write: () => Promise<void>): Promise<void> => {
    const writes = switchWrites.current;
    if (writes.inFlight === 0) writes.failed = false;
    writes.inFlight += 1;
    switchSave.begin();
    try {
      await write();
    } catch (error) {
      writes.failed = true;
      throw error;
    } finally {
      writes.inFlight -= 1;
      if (writes.inFlight === 0) {
        if (writes.failed) switchSave.fail();
        else switchSave.succeed();
      }
    }
  };

  const toggle = async (code: string, next: boolean) => {
    if (!slug) return;
    try {
      await saveSwitch(() => user.updateNotificationSetting(slug, code, next));
    } catch (error) {
      console.error("[notifications] failed to save the in-app notification switch", {
        slug,
        code,
        next,
        error,
      });
      toast.error(t("profile_settings.notifications.save_error"));
    }
  };

  const toggleTeams = async (code: string, next: boolean) => {
    if (!slug) return;
    try {
      await saveSwitch(() => user.updateTeamsSetting(slug, code, next));
    } catch (error) {
      console.error("[notifications] failed to save the Teams notification switch", {
        slug,
        code,
        next,
        error,
      });
      toast.error(t("profile_settings.notifications.save_error"));
    }
  };

  /** Ask for the Monday summary card now, and say what came back.
   *
   *  Three outcomes and three different things to say: a 202 with the counts it sent,
   *  a 200 saying nothing was due (information, not a failure — the person did nothing
   *  wrong), and a refusal whose `code` names the one thing to go and do. An
   *  unrecognised code, a 500 or a dropped connection all fall back to the generic
   *  failure rather than becoming a key fragment nobody wrote copy for.
   *
   *  Logs before it toasts even though the writer logged the throw too: the writer knows
   *  the call failed, this knows which sentence the person was shown, and a report of
   *  "it said Teams is paused" is only traceable with the code in the log line.
   */
  const sendSummaryNow = async () => {
    if (!slug) return;
    try {
      const result = await teamsLinkWriters.sendDigestNow(slug);
      if (isDigestSent(result)) {
        toast.success(
          t("profile_settings.notifications.send_summary_sent", {
            overdue: result.overdue_total,
            upcoming: result.upcoming_total,
          }),
        );
      } else {
        toast.info(t("profile_settings.notifications.send_summary_nothing_due"));
      }
    } catch (error) {
      const code = digestRefusalCode(error);
      console.error("[notifications] failed to send the Monday summary now", {
        slug,
        code,
        error,
      });
      toast.error(
        code
          ? t(`profile_settings.notifications.send_summary_refused.${code}`)
          : t("profile_settings.notifications.send_summary_error"),
      );
    }
  };

  const renderRow = (setting: INotificationSetting) => {
    const id = `notifications-${setting.code}`;
    const copy = NOTIFICATION_CATEGORY_I18N[setting.code];
    const label = copy ? t(copy.labelKey) : setting.label;
    return (
      <div key={setting.code} className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          {/* A `teams_only` row's name is an unassociated `<Label>`, deliberately: its
              only control is the Teams switch, and pointing `htmlFor` at that switch
              would make base-ui name the switch from this text (it turns the
              association into an `aria-labelledby` on the switch root, which outranks
              an `aria-label`), costing the switch the "in Teams" half of its name.
              Naming the control with `aria-label` instead is what `Label.doc.json`
              asks for when a control cannot take the association. */}
          <Label htmlFor={setting.teams_only ? undefined : id} className="text-sm font-medium">
            {label}
          </Label>
          {copy ? <p className="text-xs text-muted-foreground">{t(copy.hintKey)}</p> : null}
          {/* The **Monday summary row alone**, by its category code rather than by its
              `teams_only` shape: the backend's TEAMS_ONLY_CATEGORIES is a set designed
              to grow, and this button sends *this* category's card, so a second
              Teams-only category would otherwise draw a second button with the same
              name — two controls answering to one accessible name (NG-847), both of
              them sending the digest. It still inherits the Teams column's gating,
              because a `weekly_summary` row only renders where that column does; see
              the component docstring for why it needs no flag of its own. In the text
              column rather than the switch column: the switch column is settings, and
              this one acts now. Disabled with the row's switch off because the server
              refuses that case (`switch_off`), and while a send is out because a
              control that looks idle mid-round-trip reads as one that did nothing.
              `aria-label` repeats the visible text rather than extending it, so it
              cannot become a second element answering to the switch's name. */}
          {setting.code === WEEKLY_SUMMARY_CODE ? (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              aria-label={t("profile_settings.notifications.send_summary_now")}
              disabled={teamsLinkWriters.pendingAction === "send_digest" || !setting.teams_enabled}
              onClick={() => void sendSummaryNow()}
            >
              {t("profile_settings.notifications.send_summary_now")}
            </Button>
          ) : null}
        </div>
        <div className="flex shrink-0 items-start gap-4">
          {setting.teams_only ? null : (
            <div className={cn("flex justify-end", showTeams && COLUMN)}>
              <Switch
                id={id}
                checked={setting.enabled}
                onCheckedChange={(next) => void toggle(setting.code, next)}
              />
            </div>
          )}
          {showTeams ? (
            <div className={cn("flex justify-end", COLUMN)}>
              {/* Named for the column it is in, because the row's `<Label>` already
                  names the in-app switch — two switches answering to "Comments" is a
                  screen-reader ambiguity as much as a strict-mode violation (NG-847).
                  On a `teams_only` row it is the row's only name, which is why that
                  row's `<Label>` stays unassociated. */}
              <Switch
                aria-label={t("profile_settings.notifications.teams_switch_aria", { label })}
                checked={setting.teams_enabled}
                onCheckedChange={(next) => void toggleTeams(setting.code, next)}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <Panel className="space-y-6">
      {switchSave.touched ? (
        <div className="flex justify-end">
          <AutosaveStatusPill {...switchSave.pillProps} hideAtRest />
        </div>
      ) : null}
      {!settings ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : (
        groupIntoSections(settings, { showTeams }).map((section) => (
          <div key={section.titleKey} className="space-y-3">
            <div className="flex items-end justify-between gap-4">
              <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {t(section.titleKey)}
              </h3>
              {/* Repeated per section rather than once at the top: the sections are
                  spaced far enough apart that a single header would be off screen by
                  the time somebody reached the Proactive switches. */}
              {showTeams ? (
                <div className="flex shrink-0 items-end gap-4 text-xs text-muted-foreground">
                  {section.teamsOnly ? null : (
                    <span className={cn("text-right", COLUMN)}>
                      {t("profile_settings.notifications.column_inapp")}
                    </span>
                  )}
                  <span className={cn("text-right", COLUMN)}>
                    {t("profile_settings.notifications.column_teams")}
                  </span>
                </div>
              ) : null}
            </div>
            <div className="space-y-4">{section.items.map(renderRow)}</div>
          </div>
        ))
      )}
    </Panel>
  );
});
